import { ethers } from "ethers";
import { computeRecoveryRecordsDigest, wrapRecoveryPoolCall } from "./sdk";
import type { JoinSplitNote, SpendingKeys } from "../joinsplit/types";
import { formatInboxPublicKeyHex } from "../utils/inbox-crypto";
import { encryptOnchainNoteRecovery } from "./onchainRecoveryCodec";
import {
  configuredEvmRecoveryRegistry,
  hashEvmRecoveryRecord,
  type EvmRecoveryRecord,
} from "./onchainRecoveryEvm";
import type { FlatJoinSplitV2SwapOutputSeed } from "./swapOutput";
import type { FlatJoinSplitV2JournalDelivery } from "./pendingJournal";
import { assertFlatJoinSplitV2Note } from "./notes";

export interface EvmRecoveryOutput {
  note: JoinSplitNote;
  inboxPublicKey: string;
  /** Required when choosing a viewing key from delivery metadata or a self fallback. */
  expectedMpk?: bigint;
}

export async function encryptEvmRecoveryOutputs(
  chainId: number,
  outputs: readonly EvmRecoveryOutput[],
  seeds: readonly {
    seed: FlatJoinSplitV2SwapOutputSeed;
    pool: string;
    inboxPublicKey: string;
  }[] = [],
): Promise<EvmRecoveryRecord[]> {
  const records: EvmRecoveryRecord[] = [];
  for (const { note, inboxPublicKey, expectedMpk } of outputs) {
    if (
      note.chainId !== chainId ||
      note.schemaVersion !== 2 ||
      note.protocol !== "flat-joinsplit-v2" ||
      !note.poolAddress ||
      !note.tokenData ||
      note.tokenData.tokenType !== 0
    )
      throw Error("An output is missing its on-chain recovery domain.");
    await assertFlatJoinSplitV2Note({
      note,
      chainId: BigInt(chainId),
      poolAddress: note.poolAddress,
      expectedMpk,
    });
    const context = {
      chain: "evm" as const,
      chainDomain: ethers.toBeHex(chainId, 32),
      pool: note.poolAddress,
      kind: 1 as const,
      locator: note.commitment,
    };
    records.push({
      pool: note.poolAddress,
      kind: 1,
      locator: note.commitment,
      envelope: await encryptOnchainNoteRecovery(
        {
          kind: 1,
          random: note.random,
          value: note.value,
          tokenAddress: note.tokenData.tokenAddress,
          tokenType: 0,
        },
        inboxPublicKey,
        context,
      ),
    });
  }
  for (const { seed, pool, inboxPublicKey } of seeds) {
    const outputPool = seed.outputPoolAddress || pool;
    const context = {
      chain: "evm" as const,
      chainDomain: ethers.toBeHex(chainId, 32),
      pool: outputPool,
      kind: 2 as const,
      locator: seed.outputNpk,
    };
    records.push({
      pool: outputPool,
      kind: 2,
      locator: seed.outputNpk,
      envelope: await encryptOnchainNoteRecovery(
        {
          kind: 2,
          random: seed.random,
          tokenAddress: seed.outputTokenAddress,
          tokenType: 0,
        },
        inboxPublicKey,
        context,
      ),
    });
  }
  return records;
}

/** Never substitute a sender key for a missing external recipient viewing key. */
export function evmRecoveryOutputDeliveries(
  outputs: readonly JoinSplitNote[],
  spendingKeys: SpendingKeys,
  deliveries: readonly FlatJoinSplitV2JournalDelivery[] = [],
): EvmRecoveryOutput[] {
  const ownKey = formatInboxPublicKeyHex(spendingKeys.spendingKey);
  return outputs.map((note) => {
    const delivery = deliveries.find(
      (delivery) =>
        delivery.commitment.toLowerCase() === note.commitment.toLowerCase(),
    );
    const self =
      !delivery || BigInt(delivery.recipientMpkHex) === spendingKeys.mpk;
    const inboxPublicKey =
      delivery?.recipientInboxPublicKeyHex || (self ? ownKey : undefined);
    if (!inboxPublicKey)
      throw Error(
        "Ask the recipient for their current private address or .stealth handle before sending.",
      );
    return {
      note,
      inboxPublicKey,
      expectedMpk: delivery
        ? BigInt(delivery.recipientMpkHex)
        : spendingKeys.mpk,
    };
  });
}

export interface PreparedEvmRecovery {
  records: Array<EvmRecoveryRecord & { envelope: string }>;
  digest: string;
  wrap: (innerCall: string) => `0x${string}`;
}

/** Encrypt once before proving; the same bytes accompany the one atomic settlement. */
export async function prepareAtomicEvmRecovery(params: {
  provider: ethers.Provider;
  chainId: number;
  pool: string;
  outputs: readonly EvmRecoveryOutput[];
  seeds?: readonly {
    seed: FlatJoinSplitV2SwapOutputSeed;
    pool: string;
    inboxPublicKey: string;
  }[];
  feeOutputs?: readonly (Record<string, any> | null)[];
  signal?: AbortSignal;
}): Promise<PreparedEvmRecovery | null> {
  const configured = configuredEvmRecoveryRegistry(params.chainId);
  if (!configured) return null;
  if (params.signal?.aborted) throw Error("Recovery preparation cancelled.");
  const api = new ethers.Interface([
    "function recoveryVersion() view returns(uint256)",
    "function recoveryRegistry() view returns(address)",
  ]);
  const read = async (name: string) =>
    api.decodeFunctionResult(
      name,
      await params.provider.call({
        to: params.pool,
        data: api.encodeFunctionData(name),
      }),
    )[0];
  const [version, registry, chain, code] = await Promise.all([
    read("recoveryVersion"),
    read("recoveryRegistry"),
    params.provider.getNetwork(),
    params.provider.getCode(configured.address),
  ]);
  if (
    version !== 1n ||
    ethers.getAddress(registry) !== configured.address ||
    chain.chainId !== BigInt(params.chainId) ||
    code === "0x" ||
    ethers.keccak256(code) !== configured.runtimeCodehash
  )
    throw Error("The pool does not match the configured recovery deployment.");
  const ownRecords = await encryptEvmRecoveryOutputs(
    params.chainId,
    params.outputs,
  );
  const feeRecords = (params.feeOutputs || [])
    .filter((fee): fee is Record<string, any> => !!fee)
    .map((fee) => {
      const record = fee.recoveryRecord as EvmRecoveryRecord | undefined;
      if (
        !record ||
        record.kind !== 1 ||
        !fee.poolAddress ||
        record.pool.toLowerCase() !== fee.poolAddress.toLowerCase() ||
        record.pool.toLowerCase() !== params.pool.toLowerCase() ||
        record.locator.toLowerCase() !== String(fee.commitment).toLowerCase()
      )
        throw Error(
          "The fee quote is missing its bound encrypted recovery output.",
        );
      hashEvmRecoveryRecord(record);
      return record;
    });
  const seeds = await encryptEvmRecoveryOutputs(
    params.chainId,
    [],
    params.seeds,
  );
  const records = [...ownRecords, ...feeRecords, ...seeds].map((record) => ({
    ...record,
    envelope: ethers.hexlify(record.envelope),
  }));
  const digest = computeRecoveryRecordsDigest(
    ethers.toBeHex(params.chainId, 32),
    params.pool,
    records,
  );
  if (params.signal?.aborted) throw Error("Recovery preparation cancelled.");
  return {
    records,
    digest,
    wrap: (innerCall) => wrapRecoveryPoolCall(innerCall, records),
  };
}
